import type { ThreadDraftOwner } from '@/modules/threads/drafts'
import { useMemo, useRef } from 'react'
import { useClientPortalSummary } from '@/hooks/useClientPortalSummary'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { getCurrentClientPortalContext } from '@/lib/api/client-portal-context'
import { threadDraftOwnerKey } from '@/modules/threads/drafts'
import { portalThreadOwner } from '@/modules/threads/portal-thread-owner'
import { useOrganizationStore } from '@/store/organization.store'

import { SessionChangedError } from '@scrambl/shared/session-scope'

import { useClientPortalReadSession } from './useClientPortalReadSession'

/** One explicit original actor/workspace/client identity for reader and composer
 * routing. Portal mode derives its organization from the owned summary reader. */
export function useThreadAccessSession() {
  const scope = useWorkspaceSession(),
    selection = useOrganizationStore()
  const portalEntityId = getCurrentClientPortalContext()?.entityId ?? null
  const portal = useClientPortalReadSession(portalEntityId),
    summary = useClientPortalSummary(portalEntityId)
  const context =
    portalEntityId && portal.enabled
      ? portalThreadOwner(scope, portalEntityId, summary.data)
      : null
  const userId = scope.ownerUserId
  const organizationId = portalEntityId
    ? (context?.owner.organizationId ?? null)
    : scope.enabled
      ? scope.organizationId
      : null
  const externalRelationshipId = portalEntityId
    ? null
    : selection.currentOrganization?.isExternalAccess
      ? (selection.externalOrgRelationship?.id ?? null)
      : null
  const owner = useMemo<ThreadDraftOwner | null>(
    () =>
      userId && organizationId
        ? { userId, organizationId, portalEntityId, externalRelationshipId }
        : null,
    [userId, organizationId, portalEntityId, externalRelationshipId],
  )
  const enabled = !!threadDraftOwnerKey(owner),
    preview = context?.preview ?? false
  const identity = JSON.stringify([
      scope.key,
      owner,
      enabled,
      preview,
      portalEntityId ? summary.data?.settings : null,
    ]),
    current = useRef({ identity, generation: 0 })
  if (current.current.identity !== identity)
    current.current = { identity, generation: current.current.generation + 1 }
  const generation = current.current.generation
  return useMemo(() => {
    const assertCurrent = () => {
      scope.assertUnchanged()
      if (
        !enabled ||
        scope.signal.aborted ||
        current.current.generation !== generation ||
        (getCurrentClientPortalContext()?.entityId ?? null) !== portalEntityId
      )
        throw new SessionChangedError()
      if (portalEntityId) {
        portal.assertCurrent()
        summary.assertCurrent()
      }
    }
    return {
      owner,
      enabled,
      preview,
      key: {
        workspaceSession: scope.key,
        portalEntityId,
        ...(portalEntityId
          ? { organizationId: owner?.organizationId ?? null, preview }
          : {}),
      },
      assertCurrent,
      async run<T>(
        signal: AbortSignal,
        action: (signal: AbortSignal) => Promise<T>,
      ): Promise<T> {
        assertCurrent()
        const execute = async (linked: AbortSignal) => {
          assertCurrent()
          const result = await action(linked)
          assertCurrent()
          return result
        }
        return portalEntityId
          ? portal.run(signal, context => execute(context.signal))
          : scope.run(signal, execute)
      },
      isCurrent() {
        try {
          assertCurrent()
          return true
        } catch {
          return false
        }
      },
    }
  }, [
    scope,
    portal,
    summary.assertCurrent,
    portalEntityId,
    generation,
    enabled,
    preview,
    owner,
  ])
}
